import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLayoutTier, isStudio } from './layoutTiers';

export type StudioSlots = {
  /** Right-slot DOM node an <Aside> portals into; null while the tier has no right slot. */
  slotEl: HTMLElement | null;
  /** True while at least one <Aside> holds a claim on the right slot — the screen's own `right`
   *  steps aside. Ref-counted (a `Set` of per-instance tokens), not a bare boolean: two Asides can
   *  be mounted under one Studio (Habitat's CareTray + CompanionPickerSheet, Wardrobe's drawer +
   *  DyeSheet) and a sibling toggling closed or unmounting must not clobber another sibling that is
   *  still open. */
  asideOpen: boolean;
  /** Add (`open: true`) or remove (`open: false`) this Aside instance's own claim on the right
   *  slot. `token` must be a stable per-instance identity (e.g. `useRef({}).current`) so an Aside's
   *  own claim can be found and removed without touching any other Aside's claim. */
  claim: (token: symbol | object, open: boolean) => void;
};
export const StudioContext = createContext<StudioSlots | null>(null);

export type StudioProps = {
  /** Left panel — rendered only at studio3. */
  left?: ReactNode;
  /** The left panel's stand-in when there is no left panel: a chip row over the centre. Rendered at
   *  tablet / short / studio2, never at phone (the phone screen is the screen) or studio3. */
  fold?: ReactNode;
  /** Right panel — rendered at short / studio2 / studio3; HIDDEN (kept mounted) while an <Aside> is
   *  open, so its local state survives a sheet round-trip (fix sweep 2026-09-19: the wardrobe
   *  drawer's search/rarity used to reset after Dye because the slot unmounted it). */
  right?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Landmark labels for the two panels (fix round 1, finding 3): the Bag's left panel is
   *  navigation-within-the-screen, but Habitat's holds the pet's identity — a screen names what
   *  its panels are. Defaults keep every existing caller's labels. */
  leftLabel?: string;
  rightLabel?: string;
};

/**
 * THE STUDIO (spec §4.2). A grid of [left][centre][right] whose columns are the `--studio-l` /
 * `--studio-r` tokens; the tier decides which slots exist and this component renders only those, so
 * a panel that does not exist is not in the DOM (not merely zero-width). Owns no data and no
 * selection — screens decide what goes in each slot. Under 768 it is a plain wrapper around the
 * centre, so wrapping a phone screen in it changes nothing.
 */
export function Studio({ left, fold, right, children, className, leftLabel = 'Navigation', rightLabel = 'Detail' }: StudioProps) {
  const tier = useLayoutTier();
  const three = tier === 'studio3';
  const hasRight = isStudio(tier);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);
  const [claims, setClaims] = useState<Set<symbol | object>>(() => new Set());
  const claim = useCallback((token: symbol | object, open: boolean) => {
    setClaims((prev) => {
      const has = prev.has(token);
      if (open === has) return prev; // no-op: idempotent under <StrictMode>'s double-invoke
      const next = new Set(prev);
      if (open) next.add(token); else next.delete(token);
      return next;
    });
  }, []);
  const asideOpen = claims.size > 0;
  const ctx = useMemo<StudioSlots>(() => ({ slotEl: hasRight ? slotEl : null, asideOpen, claim }), [slotEl, asideOpen, claim, hasRight]);

  return (
    <StudioContext.Provider value={ctx}>
      <div className={`studio${className ? ` ${className}` : ''}`} data-tier={tier}>
        {three && left != null && <aside className="studio-left" aria-label={leftLabel}>{left}</aside>}
        <div className="studio-centre">
          {!three && tier !== 'phone' && fold != null && <div className="studio-fold">{fold}</div>}
          {children}
        </div>
        {hasRight && (
          <aside className="studio-right" ref={setSlotEl} aria-label={asideOpen ? undefined : rightLabel}>
            {/* `hidden`, not unmounted: the screen's own right panel keeps its state (a drawer's
                search text, a scroll offset) across an Aside opening over it. `hidden` removes it
                from layout AND the accessibility tree, so nothing behind the panel is reachable. */}
            {right != null && <div className="studio-right-screen" hidden={asideOpen}>{right}</div>}
          </aside>
        )}
      </div>
    </StudioContext.Provider>
  );
}
